import { useCallback, useEffect, useMemo, useRef } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { POSTHOG_ENABLED } from "src/app.config";

import { useLocationContext } from "@contexts/location/LocationContext";
import {
  BookingFetchResponseType,
  BookingSaveApi,
} from "@models/services/booking";
import { PaymentOptionTypes } from "@models/services/pricing";
import { applyLocalization } from "@services/common.service";
import { RootState } from "@store/index";
import type {
  TrackBookingEditedProperties,
  TrackBookingEditStartProperties,
  TrackBookingEditSubmittedProperties,
  TrackChargeRefundedProperties,
  TrackResponseErrorProperties,
} from "@utils/analytics";
import {
  analyticsLocalStorage,
  analyticsWebSessionStorage,
  clearBookingAttemptId,
  getBookingAttemptId,
  getLeadTimeHours,
  getParticipantCount,
  getPreviousPrice,
  getPriceDifference,
  getSlotsAvailableCount,
  getSport,
  mintBookingAttemptId,
  resetAnalyticsSession,
  resetUserKey,
  resolveCancellationMetrics,
  resolveUserKey,
  trackAddToCalendar,
  trackApiError,
  trackAvailabilityDwell,
  trackAvailabilityViewed,
  trackBookingCancelled,
  trackBookingConfirmed,
  trackBookingDetailsViewed,
  trackBookingEdited,
  trackBookingEditStart,
  trackBookingEditSubmitted,
  trackChargeFailed,
  trackChargeRefunded,
  trackChargeSucceeded,
  trackFavouriteToggled,
  trackFilterApplied,
  trackLocationListViewed,
  trackLocationSelected,
  trackLoginSucceeded,
  trackPaymentStepReached,
  trackRaiseNowChargeFailed,
  trackRaiseNowChargeSucceeded,
  trackRecurringBookingToggled,
  trackRegistrationStarted,
  trackRegistrationSubmitted,
  trackResponseError,
  trackSlotSelected,
  trackUserLoggedOut,
} from "@utils/analytics";

export {
  POSTHOG_ENABLED,
  clearBookingAttemptId,
  getBookingAttemptId,
  getLeadTimeHours,
  getParticipantCount,
  getPreviousPrice,
  getPriceDifference,
  getSlotsAvailableCount,
  getSport,
  mintBookingAttemptId,
  resetAnalyticsSession,
  resetUserKey,
  resolveCancellationMetrics,
  resolveUserKey,
  analyticsLocalStorage,
  analyticsWebSessionStorage,
  trackAddToCalendar,
  trackApiError,
  trackAvailabilityDwell,
  trackAvailabilityViewed,
  trackBookingCancelled,
  trackBookingConfirmed,
  trackBookingDetailsViewed,
  trackBookingEdited,
  trackBookingEditStart,
  trackBookingEditSubmitted,
  trackChargeFailed,
  trackChargeRefunded,
  trackChargeSucceeded,
  trackFavouriteToggled,
  trackFilterApplied,
  trackLocationListViewed,
  trackLocationSelected,
  trackLoginSucceeded,
  trackUserLoggedOut,
  trackPaymentStepReached,
  trackRaiseNowChargeFailed,
  trackRaiseNowChargeSucceeded,
  trackRecurringBookingToggled,
  trackRegistrationStarted,
  trackRegistrationSubmitted,
  trackResponseError,
  trackSlotSelected,
};

export type {
  TrackBookingEditedProperties,
  TrackBookingEditStartProperties,
  TrackBookingEditSubmittedProperties,
  TrackChargeRefundedProperties,
  TrackResponseErrorProperties,
};

export const REGISTRATION_ENTRY_POINT_STORAGE_KEY =
  "availio_register_entry_point";

export const REGISTRATION_ENTRY_POINTS = {
  AUTH_MODAL: "auth_modal",
  BOOKING: "booking",
  DIRECT: "direct",
  HEADER: "header",
  INVITATION: "invitation",
  LOGIN_MODAL: "login_modal",
} as const;

export type RegistrationEntryPoint =
  (typeof REGISTRATION_ENTRY_POINTS)[keyof typeof REGISTRATION_ENTRY_POINTS];

/**
 * Resolves the entry point for registration based on invitation token, session storage, or active pathname.
 */
export const getRegistrationEntryPoint = (
  invitationToken?: string,
  pathname?: string
): string => {
  if (invitationToken) return REGISTRATION_ENTRY_POINTS.INVITATION;

  const stored = analyticsWebSessionStorage.getItem(
    REGISTRATION_ENTRY_POINT_STORAGE_KEY
  );
  if (stored) {
    analyticsWebSessionStorage.removeItem(REGISTRATION_ENTRY_POINT_STORAGE_KEY);
    return stored;
  }

  const currentPath =
    pathname || (typeof window !== "undefined" ? window.location.pathname : "");
  if (currentPath.includes("booking") || currentPath.includes("organisation")) {
    return REGISTRATION_ENTRY_POINTS.BOOKING;
  }

  return REGISTRATION_ENTRY_POINTS.DIRECT;
};

/**
 * Tracks registration_started event when registration form is first rendered or first field is focused.
 */
export const useTrackRegistrationStarted = (invitationToken?: string) => {
  const hasTrackedRef = useRef<boolean>(false);
  const { pathname } = useLocation();

  const entryPoint = useMemo(
    () => getRegistrationEntryPoint(invitationToken, pathname),
    [invitationToken, pathname]
  );

  const trackStarted = useCallback(
    (customEntryPoint?: string) => {
      if (!POSTHOG_ENABLED || hasTrackedRef.current) return;
      hasTrackedRef.current = true;
      trackRegistrationStarted({
        entry_point: customEntryPoint || entryPoint,
      });
    },
    [entryPoint]
  );

  useEffect(() => {
    trackStarted();
  }, [trackStarted]);

  return {
    entryPoint,
    trackRegistrationStarted: trackStarted,
  };
};

/**
 * Tracks location_selected event once per location visit for authenticated users.
 */
export const useTrackLocation = (
  locationId?: string | number,
  sport?: string
) => {
  const { accessToken, isAuthenticated } = useSelector(
    (state: RootState) => state.local
  );
  const lastLocationTrackedRef = useRef<string>("");

  useEffect(() => {
    if (!POSTHOG_ENABLED || !locationId || (!isAuthenticated && !accessToken)) {
      return;
    }

    const locIdStr = String(locationId);
    if (lastLocationTrackedRef.current !== locIdStr) {
      const attemptId = mintBookingAttemptId();
      trackLocationSelected({
        booking_attempt_id: attemptId,
        location_id: locIdStr,
        ...(sport ? { sport } : {}),
      });
      lastLocationTrackedRef.current = locIdStr;
    }
  }, [locationId, sport, isAuthenticated, accessToken]);
};

/**
 * Tracks availability_viewed event once per date/location view when slots are loaded.
 */
export const useTrackAvailability = (
  locationId?: string | number,
  selectedDate?: string,
  slots?: BookingFetchResponseType | null,
  isLoading?: boolean,
  sport?: string
) => {
  const { accessToken, isAuthenticated } = useSelector(
    (state: RootState) => state.local
  );
  const lastAvailabilityTrackedRef = useRef<string>("");

  useEffect(() => {
    if (!POSTHOG_ENABLED || isLoading || (!isAuthenticated && !accessToken)) {
      return;
    }

    const locIdStr = String(locationId || slots?.locationId || "");
    if (!locIdStr) return;

    const key = `${locIdStr}_${selectedDate || ""}`;
    if (lastAvailabilityTrackedRef.current !== key) {
      trackAvailabilityViewed({
        location_id: locIdStr,
        slots_available_count: getSlotsAvailableCount(slots),
        ...(sport ? { sport } : {}),
      });
      lastAvailabilityTrackedRef.current = key;
    }
  }, [
    isLoading,
    locationId,
    selectedDate,
    slots,
    sport,
    isAuthenticated,
    accessToken,
  ]);
};

/**
 * Tracks booking_details_viewed event once per booking attempt.
 */
export const useTrackBookingDetails = ({
  bookingId,
  grandTotal,
  participantCount = 0,
  priceLoader,
}: {
  grandTotal?: number;
  priceLoader?: boolean;
  bookingId?: string | number;
  participantCount?: number;
}) => {
  const lastDetailsKeyRef = useRef<string>("");

  useEffect(() => {
    if (!POSTHOG_ENABLED || priceLoader || typeof grandTotal !== "number") {
      return;
    }

    const attemptId = getBookingAttemptId() || mintBookingAttemptId();
    const trackingKey = `${attemptId}_${bookingId || ""}_${participantCount}_${grandTotal}`;

    if (attemptId && lastDetailsKeyRef.current !== trackingKey) {
      trackBookingDetailsViewed({
        booking_attempt_id: attemptId,
        booking_id: bookingId,
        participant_count: participantCount,
        price: grandTotal,
      });
      lastDetailsKeyRef.current = trackingKey;
    }
  }, [priceLoader, grandTotal, bookingId, participantCount]);
};

/**
 * Tracks payment_step_reached event once per booking attempt.
 */
export const useTrackPaymentStep = ({
  bookingId,
  grandTotal,
  paymentOptions,
  priceLoader,
  selectedPaymentMethod,
}: {
  grandTotal?: number;
  paymentOptions?: PaymentOptionTypes[];
  selectedPaymentMethod?: string | null;
  bookingId?: string | number;
  priceLoader?: boolean;
}) => {
  const lastPaymentAttemptRef = useRef<string>("");

  useEffect(() => {
    if (
      !POSTHOG_ENABLED ||
      priceLoader ||
      typeof grandTotal !== "number" ||
      grandTotal <= 0 ||
      !paymentOptions ||
      paymentOptions.length === 0
    ) {
      return;
    }

    const attemptId = getBookingAttemptId() || mintBookingAttemptId();
    const offeredMethods = paymentOptions
      .map(p => (typeof p === "string" ? p : p?.type))
      .filter(Boolean) as string[];
    const offeredStr = offeredMethods.join(", ");
    const trackingKey = `${attemptId}_${bookingId || ""}_${grandTotal}_${offeredStr}_${selectedPaymentMethod || ""}`;

    if (attemptId && lastPaymentAttemptRef.current !== trackingKey) {
      trackPaymentStepReached({
        booking_attempt_id: attemptId,
        booking_id: bookingId,
        payment_method_offered: offeredStr,
        ...(selectedPaymentMethod
          ? { payment_method_selected: selectedPaymentMethod }
          : {}),
        price: grandTotal,
      });
      lastPaymentAttemptRef.current = trackingKey;
    }
  }, [
    grandTotal,
    paymentOptions,
    selectedPaymentMethod,
    bookingId,
    priceLoader,
  ]);
};

/**
 * Hook to track slot_selected safely without polluting components.
 * If POSTHOG_ENABLED is false, it returns immediately and never affects booking flow.
 */
export const useTrackSlotSelected = () => {
  const { activeLocation, locationDetails } = useLocationContext();
  const { selectedTime } = useSelector((state: RootState) => state.booking);

  const trackSlot = useCallback(
    (
      resourceId: string,
      slotDuration?: number | string,
      customSlotStart?: string
    ) => {
      if (!POSTHOG_ENABLED) return;
      try {
        const attemptId = getBookingAttemptId() || mintBookingAttemptId();
        const locationId =
          activeLocation?.id ||
          locationDetails?.locations?.[0]?.id ||
          String(locationDetails?.clubId || "0");
        const sport = activeLocation?.resourceType?.terminology?.singular
          ? applyLocalization(activeLocation.resourceType.terminology.singular)
          : undefined;

        trackSlotSelected({
          booking_attempt_id: attemptId,
          location_id: locationId,
          resource_id: resourceId,
          slot_duration: Number(slotDuration) || 0,
          slot_start: customSlotStart || selectedTime || "",
          sport,
        });
      } catch (error) {
        console.error("PostHog slot_selected tracking error:", error);
      }
    },
    [activeLocation, locationDetails, selectedTime]
  );

  return { trackSlotSelected: trackSlot };
};

/**
 * Hook to track booking edit events (booking_edit_start, booking_edit_submitted, booking_edited).
 */
export const useTrackBookingEdited = () => {
  const {
    bookingEditData,
    guestList,
    season: stateSeason,
    selectedDate,
    selectedSlot,
    selectedTime,
  } = useSelector((state: RootState) => state.booking);
  const { activeLocation, locationDetails, userProfile } = useLocationContext();

  const trackBookingEditSuccess = useCallback(
    (
      responseData?: unknown,
      payload?: BookingSaveApi,
      customAmount?: number | string,
      customSeason?: string
    ) => {
      if (!POSTHOG_ENABLED) return;
      try {
        const respObj = (responseData as { data?: Record<string, unknown> })
          ?.data;
        const resolvedBookingId = (
          typeof responseData === "string" || typeof responseData === "number"
            ? responseData
            : respObj?.bookingId ||
              respObj?.referenceId ||
              respObj?.id ||
              payload?.bookingId ||
              bookingEditData?.bookingId ||
              ""
        ) as string | number;

        const resourceId =
          selectedSlot?.resourceId || bookingEditData?.resourceId || "";
        const slotDuration = Number(selectedSlot?.slot?.slot) || 60;
        const targetDate =
          payload?.date ||
          selectedDate ||
          bookingEditData?.startDate ||
          moment().format("YYYY-MM-DD");
        const targetTime =
          selectedTime || bookingEditData?.startTime || "00:00";
        const leadTimeHours = getLeadTimeHours(targetDate, targetTime);
        const season = customSeason || stateSeason || "";

        const resolvedPrice =
          typeof customAmount === "number"
            ? customAmount
            : typeof customAmount === "string" && !isNaN(Number(customAmount))
              ? Number(customAmount)
              : typeof payload?.totalPrice === "number"
                ? payload.totalPrice
                : typeof respObj?.dueAmount === "number"
                  ? respObj.dueAmount
                  : typeof respObj?.amount === "number"
                    ? respObj.amount
                    : typeof bookingEditData?.totalPrice === "number"
                      ? bookingEditData.totalPrice
                      : 0;

        const previousSlotStart =
          bookingEditData?.startDate && bookingEditData?.startTime
            ? `${bookingEditData.startDate}T${bookingEditData.startTime}`
            : undefined;

        const previousPrice = getPreviousPrice(bookingEditData);
        const priceDifference = getPriceDifference(
          resolvedPrice,
          previousPrice
        );

        const sport = getSport(activeLocation);

        const participantCount = getParticipantCount({
          editParticipants: bookingEditData?.participants,
          guestList,
          guestPayload: payload?.guest,
        });

        trackBookingEdited({
          booking_attempt_id: getBookingAttemptId() || undefined,
          booking_category: "single",
          booking_id: resolvedBookingId,
          booking_lead_time_hours: leadTimeHours,
          booking_type: locationDetails?.isMember
            ? "internal_member"
            : "external_guest",
          edited_by: bookingEditData?.isAdminEdited ? "admin" : "user",
          location_id: activeLocation?.id || "0",
          participant_count: participantCount,
          payment_method: payload?.paymentMethod || undefined,
          previous_price: previousPrice,
          previous_resource_id: bookingEditData?.resourceId || undefined,
          previous_slot_start: previousSlotStart,
          price: resolvedPrice,
          price_difference: priceDifference,
          resource_id: resourceId,
          season,
          slot_duration: slotDuration,
          slot_start: `${targetDate}T${targetTime}`,
          sport,
          user_group:
            userProfile?.profile?.contactType ||
            (locationDetails?.isMember ? "Member" : "Guest"),
        });
      } catch (error) {
        console.error("PostHog booking_edited tracking error:", error);
      }
    },
    [
      bookingEditData,
      guestList,
      selectedDate,
      selectedSlot,
      selectedTime,
      activeLocation,
      locationDetails,
      userProfile,
      stateSeason,
    ]
  );

  const trackBookingEditStartHandler = useCallback(
    (
      bookingData?: {
        bookingId?: string | number;
        id?: string | number;
        location?: { id?: string | number };
        locationId?: string | number;
        resourceId?: string | number;
        resource?: { id?: string | number; season?: string };
        totalPrice?: number;
        basePrice?: number;
        startDate?: string;
        startTime?: string;
        participants?: unknown[];
        season?: string;
      },
      entryPoint: "reservation_card" | "direct_url" = "reservation_card",
      customSeason?: string
    ) => {
      if (!POSTHOG_ENABLED) return;
      try {
        const resolvedBookingId = (bookingData?.bookingId ||
          bookingData?.id ||
          bookingEditData?.bookingId ||
          "") as string | number;

        const locId =
          bookingData?.location?.id ||
          bookingData?.locationId ||
          bookingEditData?.location?.id ||
          activeLocation?.id ||
          "0";

        const resId =
          bookingData?.resource?.id ||
          bookingData?.resourceId ||
          bookingEditData?.resource?.id ||
          bookingEditData?.resourceId ||
          selectedSlot?.resourceId ||
          "";

        const targetDate =
          bookingData?.startDate ||
          bookingEditData?.startDate ||
          moment().format("YYYY-MM-DD");

        const targetTime =
          bookingData?.startTime || bookingEditData?.startTime || "00:00";

        const leadTimeHours = getLeadTimeHours(targetDate, targetTime);
        const season = customSeason || stateSeason || "";
        const sport = getSport(activeLocation);

        const participantCount = getParticipantCount({
          bookingParticipants: bookingData?.participants,
          editParticipants: bookingEditData?.participants,
        });

        const price =
          bookingData?.totalPrice ??
          bookingData?.basePrice ??
          bookingEditData?.totalPrice ??
          bookingEditData?.basePrice ??
          0;

        trackBookingEditStart({
          booking_category: "single",
          booking_id: resolvedBookingId,
          booking_lead_time_hours: leadTimeHours,
          booking_type: locationDetails?.isMember
            ? "internal_member"
            : "external_guest",
          entry_point: entryPoint,
          location_id: locId,
          participant_count: participantCount,
          price,
          resource_id: resId,
          season,
          slot_duration: Number(selectedSlot?.slot?.slot) || 60,
          slot_start: `${targetDate}T${targetTime}`,
          sport,
          user_group:
            userProfile?.profile?.contactType ||
            (locationDetails?.isMember ? "Member" : "Guest"),
        });
      } catch (error) {
        console.error("PostHog booking_edit_start tracking error:", error);
      }
    },
    [
      bookingEditData,
      activeLocation,
      locationDetails,
      userProfile,
      selectedSlot,
      stateSeason,
    ]
  );

  const trackBookingEditSubmittedHandler = useCallback(
    (payload?: BookingSaveApi, customSeason?: string) => {
      if (!POSTHOG_ENABLED) return;
      try {
        const resolvedBookingId = (payload?.bookingId ||
          bookingEditData?.bookingId ||
          "") as string | number;

        const resourceId =
          selectedSlot?.resourceId || bookingEditData?.resourceId || "";
        const slotDuration = Number(selectedSlot?.slot?.slot) || 60;
        const targetDate =
          payload?.date ||
          selectedDate ||
          bookingEditData?.startDate ||
          moment().format("YYYY-MM-DD");
        const targetTime =
          selectedTime || bookingEditData?.startTime || "00:00";
        const leadTimeHours = getLeadTimeHours(targetDate, targetTime);
        const season = customSeason || stateSeason || "";

        const resolvedPrice =
          typeof payload?.totalPrice === "number"
            ? payload.totalPrice
            : typeof bookingEditData?.totalPrice === "number"
              ? bookingEditData.totalPrice
              : 0;

        const previousSlotStart =
          bookingEditData?.startDate && bookingEditData?.startTime
            ? `${bookingEditData.startDate}T${bookingEditData.startTime}`
            : undefined;

        const previousPrice = getPreviousPrice(bookingEditData);
        const priceDifference = getPriceDifference(
          resolvedPrice,
          previousPrice
        );

        const sport = getSport(activeLocation);

        const participantCount = getParticipantCount({
          editParticipants: bookingEditData?.participants,
          guestList,
          guestPayload: payload?.guest,
        });

        trackBookingEditSubmitted({
          booking_attempt_id: getBookingAttemptId() || undefined,
          booking_category: "single",
          booking_id: resolvedBookingId,
          booking_lead_time_hours: leadTimeHours,
          booking_type: locationDetails?.isMember
            ? "internal_member"
            : "external_guest",
          edited_by: bookingEditData?.isAdminEdited ? "admin" : "user",
          is_amenity_edit: payload?.isAmenityEdit,
          location_id: activeLocation?.id || "0",
          participant_count: participantCount,
          payment_method: payload?.paymentMethod,
          previous_price: previousPrice,
          previous_resource_id: bookingEditData?.resourceId || undefined,
          previous_slot_start: previousSlotStart,
          price: resolvedPrice,
          price_difference: priceDifference,
          resource_id: resourceId,
          season,
          slot_duration: slotDuration,
          slot_start: `${targetDate}T${targetTime}`,
          sport,
          user_group:
            userProfile?.profile?.contactType ||
            (locationDetails?.isMember ? "Member" : "Guest"),
        });
      } catch (error) {
        console.error("PostHog booking_edit_submitted tracking error:", error);
      }
    },
    [
      bookingEditData,
      guestList,
      selectedDate,
      selectedSlot,
      selectedTime,
      activeLocation,
      locationDetails,
      userProfile,
      stateSeason,
    ]
  );

  return {
    trackBookingEdited: trackBookingEditSuccess,
    trackBookingEditStart: trackBookingEditStartHandler,
    trackBookingEditSubmitted: trackBookingEditSubmittedHandler,
    trackBookingEditSuccess,
  };
};

/**
 * Hook to track booking_confirmed safely without polluting useBooking.
 * If POSTHOG_ENABLED is false, it returns immediately and never affects booking flow.
 */
export const useTrackBookingConfirmed = () => {
  const {
    guestList,
    isEditBooking,
    season: stateSeason,
    selectedDate,
    selectedSlot,
    selectedTime,
  } = useSelector((state: RootState) => state.booking);
  const { activeLocation, locationDetails, userProfile } = useLocationContext();
  const { trackSlotSelected: trackSlot } = useTrackSlotSelected();
  const {
    trackBookingEdited: trackEdit,
    trackBookingEditStart: trackEditStart,
    trackBookingEditSubmitted: trackEditSubmitted,
  } = useTrackBookingEdited();

  const trackBookingSuccess = useCallback(
    (
      responseData?: unknown,
      payload?: BookingSaveApi,
      customAmount?: number | string,
      customSeason?: string
    ) => {
      if (!POSTHOG_ENABLED) return;
      if (isEditBooking) {
        trackEdit(responseData, payload, customAmount, customSeason);
        clearBookingAttemptId();
        return;
      }
      try {
        const respObj = (responseData as { data?: Record<string, unknown> })
          ?.data;
        const resolvedBookingId = (
          typeof responseData === "string" || typeof responseData === "number"
            ? responseData
            : respObj?.bookingId ||
              respObj?.referenceId ||
              respObj?.id ||
              payload?.bookingId ||
              ""
        ) as string | number;

        const resourceId = selectedSlot?.resourceId || "";
        const slotDuration = Number(selectedSlot?.slot?.slot) || 60;
        const targetDate =
          payload?.date || selectedDate || moment().format("YYYY-MM-DD");
        const targetTime = selectedTime || "00:00";
        const leadTimeHours = getLeadTimeHours(targetDate, targetTime);
        const season = customSeason || stateSeason || "";

        const resolvedPrice =
          typeof customAmount === "number"
            ? customAmount
            : typeof customAmount === "string" && !isNaN(Number(customAmount))
              ? Number(customAmount)
              : typeof payload?.totalPrice === "number"
                ? payload.totalPrice
                : typeof respObj?.dueAmount === "number"
                  ? respObj.dueAmount
                  : typeof respObj?.amount === "number"
                    ? respObj.amount
                    : 0;

        const sport = getSport(activeLocation);

        const participantCount = getParticipantCount({
          guestList,
          guestPayload: payload?.guest,
        });

        trackBookingConfirmed({
          booking_attempt_id: getBookingAttemptId() || undefined,
          booking_category: "single",
          booking_id: resolvedBookingId,
          booking_lead_time_hours: leadTimeHours,
          booking_type: locationDetails?.isMember
            ? "internal_member"
            : "external_guest",
          created_by: "user",
          location_id: activeLocation?.id || "0",
          participant_count: participantCount,
          payment_method: payload?.paymentMethod || undefined,
          price: resolvedPrice,
          resource_id: resourceId,
          season,
          slot_duration: slotDuration,
          slot_start: `${targetDate}T${targetTime}`,
          sport,
          user_group:
            userProfile?.profile?.contactType ||
            (locationDetails?.isMember ? "Member" : "Guest"),
        });
      } catch (error) {
        console.error("PostHog booking_confirmed tracking error:", error);
      }
    },
    [
      isEditBooking,
      trackEdit,
      guestList,
      selectedDate,
      selectedSlot,
      selectedTime,
      activeLocation,
      locationDetails,
      userProfile,
      stateSeason,
    ]
  );

  return {
    trackBookingConfirmed: trackBookingSuccess,
    trackBookingEdited: trackEdit,
    trackBookingEditStart: trackEditStart,
    trackBookingEditSubmitted: trackEditSubmitted,
    trackBookingSuccess,
    trackSlotSelected: trackSlot,
  };
};

/**
 * Main usePosthog hook providing tracking helpers and attempt ID management.
 */
export const usePosthog = () => {
  const {
    trackBookingEdited,
    trackBookingEditStart,
    trackBookingEditSubmitted,
    trackBookingSuccess,
  } = useTrackBookingConfirmed();
  return {
    clearBookingAttemptId,
    getBookingAttemptId,
    getRegistrationEntryPoint,
    isEnabled: POSTHOG_ENABLED,
    mintBookingAttemptId,
    resetAnalyticsSession,
    resetUserKey,
    resolveCancellationMetrics,
    resolveUserKey,
    trackAddToCalendar,
    trackAvailabilityDwell,
    trackAvailabilityViewed,
    trackBookingCancelled,
    trackBookingConfirmed,
    trackBookingDetailsViewed,
    trackBookingEdited,
    trackBookingEditStart,
    trackBookingEditSubmitted,
    trackBookingSuccess,
    trackChargeFailed,
    trackChargeRefunded,
    trackChargeSucceeded,
    trackFavouriteToggled,
    trackFilterApplied,
    trackLocationListViewed,
    trackLocationSelected,
    trackLoginSucceeded,
    trackPaymentStepReached,
    trackRaiseNowChargeFailed,
    trackRaiseNowChargeSucceeded,
    trackRecurringBookingToggled,
    trackRegistrationStarted,
    trackRegistrationSubmitted,
    trackSlotSelected,
    trackUserLoggedOut,
  };
};

export default usePosthog;
